import React, { useRef } from 'react';

import { RicosNodeContextProvider } from './../../ricos-context';
import type { NodeViewRendererProps } from './../../ricos-types';

import { CELL_AUTO_MIN_WIDTH } from '../consts';
import { ROOT } from '../data-hooks';
import styles from '../statics/styles/table-viewer-scss';

/*
  The width passed to 'table-layout: fixed' columns serves as a ratio between
  columns, but columns won't get smaller than their width. The smallest possible
  values are already in the colsMinWidth table data property, so we normalize
  widths to be equal to or greater than colsMinWidth so they can shrink to
  min-width and stay proportional.
  Example:
  width:     20 30
  min-width: 10 10
  ratio:     2  3
  The smallest ratio is 2, we can divide everything by 2 and still keep all widths above min-width:
  width:     10 15

  If the min-widths are not equal, the algorithm holds; we will still keep the proportions:
  width:     20 45
  min-width: 10 15
  ratio:     2  3
  The smallest ratio is 2, we can divide everything by 2 and still keep all widths above min-width:
  width:     10 22.5
*/

function normalizeColumnWidths(
  colsMinWidths: number[],
  colsRelativeWidths: number[]
): { minWidth: number; width: number }[] {
  const colsWithDefaults = colsRelativeWidths.map((originalWidth, i) => {
    const minWidth = colsMinWidths[i] || CELL_AUTO_MIN_WIDTH;
    const width = originalWidth < minWidth ? minWidth : originalWidth;
    return {
      width,
      minWidth,
    };
  });
  const ratios = colsWithDefaults.map(c => c.width / c.minWidth);
  const smallestRatio = Math.min(...ratios);
  return colsWithDefaults.map(({ minWidth, width }) => ({
    minWidth,
    width: width / smallestRatio,
  }));
}

const Table = ({
  node,
  nodeIndex,
  RicosNodesRenderer,
  NodeViewContainer,
}: NodeViewRendererProps) => {
  const tableRef = useRef<HTMLDivElement>(null);
  const { dimensions: { colsMinWidth = [], colsWidthRatio = [] } = {} } = node.tableData || {};

  const colStyles = normalizeColumnWidths(colsMinWidth, colsWidthRatio);
  return (
    <NodeViewContainer node={node} nodeIndex={nodeIndex} withHorizontalScroll>
      <div ref={tableRef} className={styles.viewMode}>
        <table className={styles.container} data-hook={ROOT}>
          <colgroup>
            {colStyles.map((colStyle, index) => {
              return <col key={index} style={colStyle} />;
            })}
          </colgroup>
          <tbody>
            <RicosNodeContextProvider parentNode={node} parentIndex={nodeIndex}>
              <RicosNodesRenderer nodes={node.nodes ?? []} />
            </RicosNodeContextProvider>
          </tbody>
        </table>
      </div>
    </NodeViewContainer>
  );
};

const TableRenderer = ({ node, ...props }: NodeViewRendererProps) => {
  return <Table key={node.id} node={node} {...props} />;
};

export default TableRenderer;
